import {
    Button,
    Input,
    InputNumber,
    Popconfirm,
    Space,
    Typography,
} from 'antd';
import { ReactNode, useState } from 'react';

import { BonusDto, BonusUpdateDto, ShiftListItemDto } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';
import { calculateTotalBonus, toDollarAmount } from '@clh/util';

import { useSelection } from '../../../hooks/use-selection';
import { useToast } from '../../../hooks/use-toast';

import { BonusFormErrors, validateBonus } from './bonus-validation';

interface BonusFormProps {
    initialAmount?: number | null;
    initialDescription?: string;
    submitLabel: string;
    onSubmit: (values: BonusUpdateDto) => Promise<void>;
    onCancel: () => void;
}

const BonusForm = ({
    initialAmount = null,
    initialDescription = '',
    submitLabel,
    onSubmit,
    onCancel,
}: BonusFormProps) => {
    const [amount, setAmount] = useState<number | null>(initialAmount);
    const [description, setDescription] = useState(initialDescription);
    const [errors, setErrors] = useState<BonusFormErrors>({});
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const validationErrors = validateBonus({ amount, description });

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            await onSubmit({
                amount: Math.round(amount! * 100),
                description: description.trim(),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space size="small" align="start">
                <div style={{ width: 150 }}>
                    <InputNumber
                        placeholder="Amount"
                        prefix="$"
                        style={{ width: '100%' }}
                        min={0}
                        value={amount}
                        onChange={setAmount}
                    />
                    {errors.amount && (
                        <div>
                            <Typography.Text type="danger">
                                {errors.amount}
                            </Typography.Text>
                        </div>
                    )}
                </div>
                <div style={{ width: 260 }}>
                    <Input
                        placeholder="Description"
                        maxLength={100}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                    {errors.description && (
                        <div>
                            <Typography.Text type="danger">
                                {errors.description}
                            </Typography.Text>
                        </div>
                    )}
                </div>
            </Space>

            <Typography.Text type="secondary">
                The nurse is paid this full amount. The facility is billed this
                plus the platform fee and employer taxes.
            </Typography.Text>

            <Space size="small">
                <Button
                    type="primary"
                    loading={loading}
                    onClick={() => void handleSubmit()}
                >
                    {submitLabel}
                </Button>
                <Button onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
            </Space>
        </Space>
    );
};

interface AddBonusFormProps {
    onCancel: () => void;
    onSuccess: () => void;
}

const AddBonusForm = ({ onCancel, onSuccess }: AddBonusFormProps) => {
    const api = useApiClient();
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const { notification } = useToast();

    const handleAdd = async ({ amount, description }: BonusUpdateDto) => {
        try {
            const updatedShift = await api?.bonusControllerAddBonus({
                bonusCreateDto: { shiftId: selection.id, amount, description },
            });

            if (updatedShift?.bonuses) {
                setSelection({ ...selection, bonuses: updatedShift.bonuses });
            }

            notification?.success({ message: 'Bonus added' });
            onSuccess();
        } catch (error) {
            notification?.error({
                message: 'Failed to add bonus',
                description: unwrapError(error),
            });
        }
    };

    return (
        <BonusForm submitLabel="Add" onSubmit={handleAdd} onCancel={onCancel} />
    );
};

interface BonusItemProps {
    bonus: BonusDto;
    disabled: boolean;
}

const BonusItem = ({ bonus, disabled }: BonusItemProps) => {
    const api = useApiClient();
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const { notification } = useToast();
    const [editing, setEditing] = useState(false);
    const [removing, setRemoving] = useState(false);

    const handleSave = async (bonusUpdateDto: BonusUpdateDto) => {
        try {
            const updatedBonus = await api?.bonusControllerUpdateBonus({
                bonusId: bonus.id,
                bonusUpdateDto,
            });

            if (updatedBonus) {
                setSelection({
                    ...selection,
                    bonuses: selection.bonuses?.map((existing) =>
                        existing.id === bonus.id ? updatedBonus : existing
                    ),
                });
            }

            notification?.success({ message: 'Bonus updated' });
            setEditing(false);
        } catch (error) {
            notification?.error({
                message: 'Failed to update bonus',
                description: unwrapError(error),
            });
        }
    };

    const handleRemove = async () => {
        setRemoving(true);
        try {
            await api?.bonusControllerDeleteBonus({ bonusId: bonus.id });

            setSelection({
                ...selection,
                bonuses: selection.bonuses?.filter(
                    (existing) => existing.id !== bonus.id
                ),
            });

            notification?.success({ message: 'Bonus removed' });
        } catch (error) {
            notification?.error({
                message: 'Failed to remove bonus',
                description: unwrapError(error),
            });
        } finally {
            setRemoving(false);
        }
    };

    if (editing) {
        return (
            <BonusForm
                initialAmount={bonus.amount / 100}
                initialDescription={bonus.description}
                submitLabel="Save"
                onSubmit={handleSave}
                onCancel={() => setEditing(false)}
            />
        );
    }

    return (
        <div style={{ textAlign: 'right' }}>
            {bonus.description} {toDollarAmount(bonus.amount)}
            {!disabled && (
                <>
                    <Button
                        size="small"
                        style={{ marginLeft: 10 }}
                        onClick={() => setEditing(true)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Remove this bonus?"
                        description={`The nurse's pay drops by ${toDollarAmount(bonus.amount)}.`}
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                        onConfirm={() => void handleRemove()}
                    >
                        <Button
                            danger
                            size="small"
                            style={{ marginLeft: 10 }}
                            loading={removing}
                        >
                            &times;
                        </Button>
                    </Popconfirm>
                </>
            )}
        </div>
    );
};

export const createBonusesFieldRenderer = (
    selection: ShiftListItemDto,
    isAddingBonus: boolean,
    onStartAdd: () => void,
    onCancelAdd: () => void
): (() => ReactNode) => {
    const renderBonuses = () => {
        const bonuses = selection.bonuses ?? [];
        const isDisabled = !!selection.submittedTimeAt;

        return (
            <div>
                {bonuses.length === 0 && !isAddingBonus && (
                    <div>No bonuses on this shift.</div>
                )}

                {bonuses.map((bonus) => (
                    <BonusItem
                        key={bonus.id}
                        bonus={bonus}
                        disabled={isDisabled}
                    />
                ))}

                {bonuses.length > 0 && (
                    <div style={{ textAlign: 'right' }}>
                        <Typography.Text strong>
                            Total {toDollarAmount(calculateTotalBonus(bonuses))}
                        </Typography.Text>
                    </div>
                )}

                {isDisabled ? (
                    <Typography.Text type="secondary">
                        Time has been submitted for this shift, so bonuses can
                        no longer be changed.
                    </Typography.Text>
                ) : isAddingBonus ? (
                    <AddBonusForm
                        onCancel={onCancelAdd}
                        onSuccess={onCancelAdd}
                    />
                ) : (
                    <a onClick={onStartAdd}>Add bonus</a>
                )}
            </div>
        );
    };

    renderBonuses.displayName = 'BonusesFieldRenderer';
    return renderBonuses;
};
